import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getWorkflow,
  updateWorkflow,
  type Workflow,
  type WorkflowNode,
  type WorkflowEdge,
} from "../api/client.js";
import { NodePalette } from "./NodePalette.js";
import { NodeConfigPanel } from "./NodeConfigPanel.js";
import { useDAGState, autoLayout, validateDAGClient } from "./useDAGState.js";
import type { NodePosition } from "./useDAGState.js";

const NODE_WIDTH = 160;
const NODE_HEIGHT = 56;
const PORT_RADIUS = 6;
const NODE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  tool_call: { bg: "#eff6ff", border: "#3b82f6", text: "#1e40af" },
  llm_call: { bg: "#f0fdf4", border: "#22c55e", text: "#166534" },
  conditional: { bg: "#fefce8", border: "#eab308", text: "#854d0e" },
  human_approval: { bg: "#fdf2f8", border: "#ec4899", text: "#9d174d" },
  transform: { bg: "#f5f3ff", border: "#8b5cf6", text: "#5b21b6" },
};

interface DragState {
  nodeId: string;
  offsetX: number;
  offsetY: number;
}

interface EdgeDraft {
  fromNodeId: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}

export function DAGEditor() {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [drag, setDrag] = useState<DragState | null>(null);
  const [edgeDraft, setEdgeDraft] = useState<EdgeDraft | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [state, actions] = useDAGState();

  // Load workflow
  useEffect(() => {
    if (!workflowId) return;
    getWorkflow(workflowId)
      .then((wf) => {
        setWorkflow(wf);
        const positions = autoLayout(wf.definition.nodes, wf.definition.edges);
        actions.replaceAll(wf.definition.nodes, wf.definition.edges, positions);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [workflowId]);

  // Validate on every change
  useEffect(() => {
    if (state.nodes.length === 0) {
      setValidationErrors([]);
      return;
    }
    const result = validateDAGClient(state.nodes, state.edges);
    setValidationErrors(result.errors);
  }, [state.nodes, state.edges]);

  // ── Node Drag ─────────────────────────────────────────────────────────────

  const handleNodeMouseDown = useCallback(
    (nodeId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const pos = state.positions.get(nodeId);
      if (!pos) return;
      setDrag({ nodeId, offsetX: e.clientX - pos.x, offsetY: e.clientY - pos.y });
      actions.selectNode(nodeId);
      actions.selectEdge(null);
    },
    [state.positions, actions],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (drag) {
        actions.setPosition(drag.nodeId, {
          x: e.clientX - drag.offsetX,
          y: e.clientY - drag.offsetY,
        });
      } else if (edgeDraft) {
        setEdgeDraft((prev) =>
          prev ? { ...prev, toX: e.nativeEvent.offsetX, toY: e.nativeEvent.offsetY } : null,
        );
      }
    },
    [drag, edgeDraft, actions],
  );

  const handleMouseUp = useCallback(() => {
    setDrag(null);
    setEdgeDraft(null);
  }, []);

  // ── Edge Creation via Port ────────────────────────────────────────────────

  const handleOutputPortMouseDown = useCallback(
    (nodeId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const pos = state.positions.get(nodeId);
      if (!pos) return;
      const fromX = pos.x + NODE_WIDTH / 2;
      const fromY = pos.y + NODE_HEIGHT;
      setEdgeDraft({ fromNodeId: nodeId, fromX, fromY, toX: fromX, toY: fromY });
    },
    [state.positions],
  );

  const handleInputPortMouseUp = useCallback(
    (nodeId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (edgeDraft && edgeDraft.fromNodeId !== nodeId) {
        const wouldCycle = checkCycle(state.nodes, state.edges, edgeDraft.fromNodeId, nodeId);
        if (!wouldCycle) {
          actions.addEdge(edgeDraft.fromNodeId, nodeId);
        }
      }
      setEdgeDraft(null);
    },
    [edgeDraft, state.nodes, state.edges, actions],
  );

  // ── Drag-to-Canvas (from palette) ────────────────────────────────────────

  const handleCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleCanvasDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const nodeType = e.dataTransfer.getData("application/flax-node-type");
      if (!nodeType) return;
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = e.clientX - rect.left - NODE_WIDTH / 2;
      const y = e.clientY - rect.top - NODE_HEIGHT / 2;
      actions.addNode(nodeType, { x: Math.max(0, x), y: Math.max(0, y) });
    },
    [actions],
  );

  // ── Keyboard shortcuts ────────────────────────────────────────────────────

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Delete" || e.key === "Backspace") {
        // Don't capture if typing in an input
        if ((e.target as HTMLElement).tagName === "INPUT" || (e.target as HTMLElement).tagName === "TEXTAREA" || (e.target as HTMLElement).tagName === "SELECT") return;
        if (state.selectedNodeId) {
          actions.removeNode(state.selectedNodeId);
        } else if (state.selectedEdgeIndex !== null) {
          actions.removeEdge(state.selectedEdgeIndex);
        }
      }
      if (e.key === "Escape") {
        actions.selectNode(null);
        actions.selectEdge(null);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [state.selectedNodeId, state.selectedEdgeIndex, actions]);

  // ── Auto-layout ───────────────────────────────────────────────────────────

  const handleAutoLayout = useCallback(() => {
    // Strip saved positions to force recalculation
    const nodesWithoutPos = state.nodes.map((n) => ({ ...n, position: undefined }));
    const newPositions = autoLayout(nodesWithoutPos, state.edges);
    actions.setPositions(newPositions);
  }, [state.nodes, state.edges, actions]);

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!workflow) return;
    if (validationErrors.length > 0 && state.nodes.length > 0) return;

    setSaving(true);
    setSaveError("");
    try {
      // Embed positions into node definitions for persistence
      const nodesWithPositions = state.nodes.map((n) => ({
        ...n,
        position: state.positions.get(n.id),
      }));
      const updated = await updateWorkflow(workflow.id, {
        definition: { nodes: nodesWithPositions, edges: state.edges },
      });
      setWorkflow(updated);
      actions.markClean();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [workflow, state.nodes, state.edges, state.positions, validationErrors, actions]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink-300 border-t-ink-800" />
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-sm font-semibold text-ink-800">Workflow not found</h2>
          <button onClick={() => navigate("/workflows")} className="btn-secondary mt-3 text-xs">
            Back to workflows
          </button>
        </div>
      </div>
    );
  }

  const selectedNode = state.selectedNodeId
    ? state.nodes.find((n) => n.id === state.selectedNodeId) ?? null
    : null;

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-ink-100 bg-white px-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/workflows")}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-400 hover:bg-surface-2 hover:text-ink-700"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M10 4L6 8l4 4" />
            </svg>
          </button>
          <h1 className="text-sm font-semibold text-ink-900">{workflow.name}</h1>
          <span className="badge-gray font-mono text-2xs">{workflow.id.slice(0, 8)}</span>
          {state.dirty && <span className="badge-amber text-2xs">Unsaved</span>}
        </div>
        <div className="flex items-center gap-2">
          {validationErrors.length > 0 && (
            <span className="text-2xs text-danger" title={validationErrors.join("\n")}>
              {validationErrors.length} error{validationErrors.length !== 1 ? "s" : ""}
            </span>
          )}
          <span className="text-2xs text-ink-400">
            {state.nodes.length} node{state.nodes.length !== 1 ? "s" : ""} ·{" "}
            {state.edges.length} edge{state.edges.length !== 1 ? "s" : ""}
          </span>
          <button onClick={handleAutoLayout} className="btn-ghost text-xs" title="Auto-layout">
            <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="1" y="1" width="5" height="5" rx="1" />
              <rect x="10" y="1" width="5" height="5" rx="1" />
              <rect x="5.5" y="10" width="5" height="5" rx="1" />
              <path d="M3.5 6v2h9V6M8 8v2" />
            </svg>
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !state.dirty || (validationErrors.length > 0 && state.nodes.length > 0)}
            className="btn-primary text-xs px-3"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </header>

      {saveError && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-2 text-xs text-danger">
          {saveError}
        </div>
      )}

      {/* Main area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Palette */}
        <NodePalette onDragStart={() => {}} />

        {/* Center: Canvas */}
        <div
          ref={canvasRef}
          className="relative flex-1 overflow-hidden bg-surface-1"
          onDragOver={handleCanvasDragOver}
          onDrop={handleCanvasDrop}
        >
          {/* Grid */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "radial-gradient(circle, #e2e8f0 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />

          {state.nodes.length === 0 ? (
            <div className="relative flex h-full items-center justify-center">
              <div className="text-center">
                <p className="text-sm text-ink-500">Drag nodes from the palette to start</p>
                <p className="mt-1 text-xs text-ink-400">Connect outputs to inputs to create edges</p>
              </div>
            </div>
          ) : (
            <svg
              ref={svgRef}
              className="relative h-full w-full"
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onClick={() => {
                actions.selectNode(null);
                actions.selectEdge(null);
              }}
            >
              <defs>
                <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#94a3b8" />
                </marker>
                <marker id="arrow-active" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#3b82f6" />
                </marker>
                <marker id="arrow-draft" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#94a3b8" opacity="0.5" />
                </marker>
              </defs>

              {/* Edges */}
              {state.edges.map((edge, i) => {
                const from = state.positions.get(edge.from);
                const to = state.positions.get(edge.to);
                if (!from || !to) return null;

                const x1 = from.x + NODE_WIDTH / 2;
                const y1 = from.y + NODE_HEIGHT;
                const x2 = to.x + NODE_WIDTH / 2;
                const y2 = to.y;
                const midY = (y1 + y2) / 2;
                const isSelected = state.selectedEdgeIndex === i;

                return (
                  <g key={`edge-${i}`}>
                    {/* Invisible wide hitbox for easier clicking */}
                    <path
                      d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`}
                      fill="none"
                      stroke="transparent"
                      strokeWidth="12"
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        actions.selectEdge(i);
                        actions.selectNode(null);
                      }}
                    />
                    <path
                      d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`}
                      fill="none"
                      stroke={isSelected ? "#3b82f6" : "#94a3b8"}
                      strokeWidth={isSelected ? 2 : 1.5}
                      markerEnd={isSelected ? "url(#arrow-active)" : "url(#arrow)"}
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        actions.selectEdge(i);
                        actions.selectNode(null);
                      }}
                    />
                    {edge.condition && (
                      <text
                        x={(x1 + x2) / 2}
                        y={midY - 6}
                        textAnchor="middle"
                        className="fill-ink-400 text-[10px]"
                      >
                        {edge.condition}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Edge draft (being drawn) */}
              {edgeDraft && (
                <path
                  d={`M ${edgeDraft.fromX} ${edgeDraft.fromY} L ${edgeDraft.toX} ${edgeDraft.toY}`}
                  fill="none"
                  stroke="#94a3b8"
                  strokeWidth="1.5"
                  strokeDasharray="6 3"
                  markerEnd="url(#arrow-draft)"
                  pointerEvents="none"
                />
              )}

              {/* Nodes */}
              {state.nodes.map((node) => {
                const pos = state.positions.get(node.id);
                if (!pos) return null;
                const colors = NODE_COLORS[node.type] ?? { bg: "#f8fafc", border: "#94a3b8", text: "#334155" };
                const isSelected = state.selectedNodeId === node.id;

                return (
                  <g key={node.id}>
                    {/* Input port (top center) */}
                    <circle
                      cx={pos.x + NODE_WIDTH / 2}
                      cy={pos.y}
                      r={PORT_RADIUS}
                      fill={edgeDraft ? "#60a5fa" : "#e2e8f0"}
                      stroke="#94a3b8"
                      strokeWidth="1"
                      className="cursor-crosshair"
                      onMouseUp={(e) => handleInputPortMouseUp(node.id, e)}
                    />

                    {/* Node body */}
                    <g
                      onMouseDown={(e) => handleNodeMouseDown(node.id, e)}
                      style={{ cursor: drag?.nodeId === node.id ? "grabbing" : "grab" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <rect
                        x={pos.x + 1}
                        y={pos.y + 2}
                        width={NODE_WIDTH}
                        height={NODE_HEIGHT}
                        rx={12}
                        fill="rgba(0,0,0,0.06)"
                      />
                      <rect
                        x={pos.x}
                        y={pos.y}
                        width={NODE_WIDTH}
                        height={NODE_HEIGHT}
                        rx={12}
                        fill={colors.bg}
                        stroke={isSelected ? colors.border : "#e2e8f0"}
                        strokeWidth={isSelected ? 2 : 1}
                      />
                      <text
                        x={pos.x + 12}
                        y={pos.y + 18}
                        className="text-[9px] font-medium uppercase"
                        fill={colors.text}
                      >
                        {node.type.replace("_", " ")}
                      </text>
                      <text
                        x={pos.x + 12}
                        y={pos.y + 38}
                        className="text-[12px] font-medium"
                        fill="#1e293b"
                      >
                        {(node.label ?? node.id).length > 18
                          ? (node.label ?? node.id).slice(0, 16) + "..."
                          : (node.label ?? node.id)}
                      </text>
                    </g>

                    {/* Output port (bottom center) */}
                    <circle
                      cx={pos.x + NODE_WIDTH / 2}
                      cy={pos.y + NODE_HEIGHT}
                      r={PORT_RADIUS}
                      fill="#e2e8f0"
                      stroke="#94a3b8"
                      strokeWidth="1"
                      className="cursor-crosshair"
                      onMouseDown={(e) => handleOutputPortMouseDown(node.id, e)}
                    />
                  </g>
                );
              })}
            </svg>
          )}
        </div>

        {/* Right: Config panel */}
        {selectedNode && (
          <NodeConfigPanel
            node={selectedNode}
            onUpdate={actions.updateNodeConfig}
            onDelete={actions.removeNode}
            onClose={() => actions.selectNode(null)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Check if adding an edge from→to would create a cycle.
 */
function checkCycle(
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
  from: string,
  to: string,
): boolean {
  // Would the new edge create a path from `to` back to `from`?
  const adj = new Map<string, string[]>();
  for (const n of nodes) adj.set(n.id, []);
  for (const e of edges) adj.get(e.from)?.push(e.to);
  // Add the proposed edge
  adj.get(from)?.push(to);

  // BFS from `to` to see if we can reach `from`
  const visited = new Set<string>();
  const queue = [to];
  while (queue.length > 0) {
    const curr = queue.shift()!;
    if (curr === from) return true;
    if (visited.has(curr)) continue;
    visited.add(curr);
    for (const next of adj.get(curr) ?? []) {
      queue.push(next);
    }
  }
  return false;
}
